import React, { useId } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";

// CMP Dashboard (24) CD:10003-10016 (userConfirm): the Suspend, Unsuspend and
// Delete Employee Access pop-up. 460px card, 72px circle with a white warning
// triangle (red #F21B1B, gold #E1A731 for Unsuspend), 16px/800 uppercase
// title, 14px message, and two 14px/700 buttons in title case. The shared
// ConfirmDialog draws a 440px card with a 64px circle, so this pop-up has its
// own shell (live check Nov 1 2026: blue ban icon and UPPERCASE buttons).

export type EmployeeAccessConfirmTone = "red" | "gold";

const toneColors: Record<EmployeeAccessConfirmTone, string> = {
  red: "#F21B1B",
  gold: "#E1A731",
};

const buttonStyle: React.CSSProperties = {
  alignItems: "center",
  border: "none",
  borderRadius: 10,
  color: "#FFFFFF",
  cursor: "pointer",
  display: "inline-flex",
  flex: "1 1 0",
  fontFamily: "'DM Sans', sans-serif",
  fontSize: 14,
  fontWeight: 700,
  gap: 8,
  justifyContent: "center",
  maxWidth: 180,
  padding: "13px 20px",
};

export type EmployeeAccessConfirmDialogProps = {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  tone: EmployeeAccessConfirmTone;
  loading?: boolean;
  onConfirm: () => void;
  onClose: () => void;
};

export const EmployeeAccessConfirmDialog = ({
  open,
  title,
  message,
  confirmLabel,
  tone,
  loading = false,
  onConfirm,
  onClose,
}: EmployeeAccessConfirmDialogProps) => {
  const accessibleId = useId();
  const titleId = `employee-access-confirm-title-${accessibleId}`;
  const messageId = `employee-access-confirm-message-${accessibleId}`;
  const color = toneColors[tone];
  const handleClose = () => {
    if (!loading) onClose();
  };
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      aria-labelledby={titleId}
      aria-describedby={messageId}
      sx={{
        zIndex: 4300,
        "& .MuiDialog-container": { boxSizing: "border-box", padding: "20px" },
      }}
      BackdropProps={{ sx: { backgroundColor: "rgba(10, 10, 10, 0.45)" } }}
      PaperProps={{
        sx: {
          alignItems: "center",
          backgroundColor: "#FFFFFF",
          border: "1px solid #E5E7EB",
          borderRadius: "14px",
          boxShadow: "0 18px 48px rgba(0, 0, 0, 0.18)",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          fontFamily: "'DM Sans', sans-serif",
          margin: 0,
          maxWidth: "none",
          padding: "30px 28px",
          position: "relative",
          width: "min(460px, 92vw)",
        },
      }}
    >
      <IconButton
        aria-label="Close"
        disabled={loading}
        onClick={handleClose}
        sx={{
          backgroundColor: "#F21B1B",
          height: 28,
          padding: 0,
          position: "absolute",
          right: 14,
          top: 14,
          width: 28,
          "&:hover": { backgroundColor: "#D01414" },
        }}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" style={{ width: 13, height: 13 }} aria-hidden="true">
          <path d="m6 6 12 12M18 6 6 18" />
        </svg>
      </IconButton>
      <div
        data-testid="employee-access-confirm-circle"
        style={{
          alignItems: "center",
          backgroundColor: color,
          borderRadius: "50%",
          display: "flex",
          flexShrink: 0,
          height: 72,
          justifyContent: "center",
          width: 72,
        }}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: "50%", height: "50%" }} aria-hidden="true">
          <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
          <path d="M12 9v4M12 17h.01" />
        </svg>
      </div>
      <h2
        id={titleId}
        style={{
          color: "#0A0A0A",
          fontSize: 16,
          fontWeight: 800,
          letterSpacing: ".02em",
          margin: "18px 0 0",
          textAlign: "center",
          textTransform: "uppercase",
        }}
      >
        {title}
      </h2>
      <div
        id={messageId}
        style={{
          color: "#0A0A0A",
          fontSize: 14,
          lineHeight: 1.5,
          marginTop: 8,
          textAlign: "center",
          textWrap: "pretty",
        }}
      >
        {message}
      </div>
      <div style={{ display: "flex", gap: 14, justifyContent: "center", marginTop: 22, width: "100%" }}>
        <button
          type="button"
          disabled={loading}
          onClick={onConfirm}
          style={{ ...buttonStyle, backgroundColor: color, opacity: loading ? 0.6 : 1 }}
        >
          {loading ? <CircularProgress color="inherit" size={16} /> : null}
          {confirmLabel}
        </button>
        <button
          type="button"
          disabled={loading}
          onClick={handleClose}
          style={{ ...buttonStyle, backgroundColor: "#0A0A0A", opacity: loading ? 0.6 : 1 }}
        >
          Cancel
        </button>
      </div>
    </Dialog>
  );
};

export default EmployeeAccessConfirmDialog;
